import React, { useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";

function cx(...xs) {
  return xs.filter(Boolean).join(" ");
}

function PasswordInput({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={cx(
        `
          w-full rounded-2xl
          border border-[rgb(var(--border))]
          bg-[rgb(var(--bg-app))]
          px-4 py-3.5
          text-[rgb(var(--text-main))]
          placeholder:text-[rgb(var(--text-muted))]
          outline-none transition
          focus:border-[rgb(var(--brand)/0.55)]
          focus:ring-4 focus:ring-[rgb(var(--brand)/0.10)]
        `,
        className
      )}
    />
  );
}

function PrimaryButton({ children, disabled }) {
  return (
    <button
      type="submit"
      disabled={disabled}
      className={cx(
        `
          inline-flex w-full items-center justify-center
          rounded-2xl border
          px-5 py-3.5
          text-sm font-extrabold
          transition
        `,
        disabled
          ? `
              cursor-not-allowed
              border-[rgb(var(--border))]
              bg-[rgb(var(--elevated))]
              text-[rgb(var(--text-muted))]
            `
          : `
              border-[rgb(var(--brand)/0.35)]
              bg-[rgb(var(--brand))]
              text-white
              shadow-[var(--shadow-soft)]
              hover:-translate-y-0.5
              hover:opacity-90
            `
      )}
    >
      {children}
    </button>
  );
}

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const hasToken = Boolean(token);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [error, setError] = useState("");

  const passwordRegex =
    /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_\-+=<>?/{}~[\]|\\]).{8,}$/;

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");
    setSuccessMessage("");

    if (!token) {
      setError("Missing reset token.");
      return;
    }

    if (!passwordRegex.test(password)) {
      setError(
        "Password must be at least 8 characters and include an uppercase letter, a number, and a special character."
      );
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);

    try {
      const res = await axios.post("/api/users/reset-password", {
        token,
        password,
      });

      setSuccessMessage(
        res.data?.message ||
          "Your password has been updated successfully. You can now open Veacon and sign in."
      );

      setPassword("");
      setConfirmPassword("");
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "Failed to reset password."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="
        relative min-h-screen overflow-hidden
        bg-[rgb(var(--bg-app))]
        text-[rgb(var(--text-main))]
      "
    >
      {/* Veacon accent */}
      <div
        className="
          h-1.5 w-full
          bg-[linear-gradient(90deg,rgb(var(--brand)),rgb(var(--cool)),rgb(var(--intent)))]
        "
      />

      {/* Background glow */}
      <div
        className="
          pointer-events-none absolute
          left-1/2 top-[-260px]
          h-[560px] w-[560px]
          -translate-x-1/2
          rounded-full blur-3xl
        "
        style={{
          background:
            "radial-gradient(circle, rgb(var(--brand) / 0.10), transparent 68%)",
        }}
      />

      <main
        className="
          relative flex min-h-[calc(100vh-6px)]
          items-center justify-center
          px-4 py-10
          sm:px-6
        "
      >
        <div className="w-full max-w-md">
          {/* Brand */}
          <Link
            to="/"
            aria-label="Veacon home"
            className="
              mx-auto mb-8
              flex w-fit items-center gap-3
            "
          >
            <div
              className="
                flex h-12 w-12 items-center justify-center
                overflow-hidden rounded-2xl
                border border-[rgb(var(--border))]
                bg-[rgb(var(--surface))]
                shadow-[var(--shadow-soft)]
              "
            >
              <img
                src="/logo.svg"
                alt=""
                className="h-7 w-7"
              />
            </div>

            <div>
              <div
                className="
                  text-base font-extrabold
                  tracking-tight
                  text-[rgb(var(--text-main))]
                "
              >
                Veacon
              </div>

              <div
                className="
                  text-xs font-semibold
                  text-[rgb(var(--text-muted))]
                "
              >
                Find things to do.
              </div>
            </div>
          </Link>

          {/* Card */}
          <section
            className="
              rounded-[2rem]
              border border-[rgb(var(--border))]
              bg-[rgb(var(--surface))]
              p-6
              shadow-[var(--shadow-soft)]
              sm:p-8
            "
          >
            {successMessage ? (
              <div className="text-center">
                <div
                  className="
                    mx-auto flex h-14 w-14
                    items-center justify-center
                    rounded-2xl
                    bg-[rgb(var(--brand)/0.10)]
                    text-xl font-black
                    text-[rgb(var(--brand))]
                  "
                  aria-hidden="true"
                >
                  ✓
                </div>

                <h1
                  className="
                    mt-5
                    text-3xl font-extrabold
                    tracking-[-0.04em]
                    text-[rgb(var(--text-main))]
                  "
                >
                  Password updated
                </h1>

                <p
                  className="
                    mx-auto mt-3 max-w-sm
                    text-sm leading-6
                    text-[rgb(var(--text-muted))]
                  "
                >
                  {successMessage}
                </p>

                <Link
                  to="/"
                  className="
                    mt-7 inline-flex w-full
                    items-center justify-center
                    rounded-2xl
                    border border-[rgb(var(--brand)/0.35)]
                    bg-[rgb(var(--brand))]
                    px-5 py-3.5
                    text-sm font-extrabold text-white
                    shadow-[var(--shadow-soft)]
                    transition
                    hover:-translate-y-0.5
                    hover:opacity-90
                  "
                >
                  Return to Veacon
                </Link>
              </div>
            ) : (
              <>
                <div className="text-center">
                  <div
                    className="
                      mx-auto flex h-12 w-12
                      items-center justify-center
                      rounded-2xl
                      bg-[rgb(var(--brand)/0.10)]
                      text-xl
                    "
                    aria-hidden="true"
                  >
                    •••
                  </div>

                  <h1
                    className="
                      mt-5
                      text-3xl font-extrabold
                      tracking-[-0.04em]
                      text-[rgb(var(--text-main))]
                    "
                  >
                    Choose a new password
                  </h1>

                  <p
                    className="
                      mx-auto mt-3 max-w-sm
                      text-sm leading-6
                      text-[rgb(var(--text-muted))]
                    "
                  >
                    Create a new password for your Veacon
                    account.
                  </p>
                </div>

                {!hasToken ? (
                  <div
                    className="
                      mt-6 rounded-2xl
                      border border-[rgb(var(--danger)/0.30)]
                      bg-[rgb(var(--danger)/0.08)]
                      p-4
                    "
                  >
                    <div
                      className="
                        text-sm font-extrabold
                        text-[rgb(var(--text-main))]
                      "
                    >
                      Invalid reset link
                    </div>

                    <div
                      className="
                        mt-1 text-sm leading-6
                        text-[rgb(var(--text-muted))]
                      "
                    >
                      This password reset link is missing a
                      token or is no longer valid.
                    </div>

                    <Link
                      to="/forgot-password"
                      className="
                        mt-3 inline-flex
                        text-sm font-bold
                        text-[rgb(var(--text-main))]
                        underline
                        decoration-[rgb(var(--border))]
                        underline-offset-4
                        transition
                        hover:text-[rgb(var(--brand))]
                      "
                    >
                      Request a new reset link
                    </Link>
                  </div>
                ) : null}

                {error ? (
                  <div
                    className="
                      mt-6 rounded-2xl
                      border border-[rgb(var(--danger)/0.30)]
                      bg-[rgb(var(--danger)/0.08)]
                      p-4
                    "
                  >
                    <div
                      className="
                        text-sm font-extrabold
                        text-[rgb(var(--text-main))]
                      "
                    >
                      Couldn&apos;t reset password
                    </div>

                    <div
                      className="
                        mt-1 text-sm leading-6
                        text-[rgb(var(--text-muted))]
                      "
                    >
                      {error}
                    </div>
                  </div>
                ) : null}

                {hasToken ? (
                  <form
                    onSubmit={handleSubmit}
                    className="mt-7 space-y-5"
                  >
                    <div>
                      <label
                        htmlFor="new-password"
                        className="
                          text-sm font-bold
                          text-[rgb(var(--text-main))]
                        "
                      >
                        New password
                      </label>

                      <PasswordInput
                        id="new-password"
                        type="password"
                        value={password}
                        onChange={(e) =>
                          setPassword(e.target.value)
                        }
                        autoComplete="new-password"
                        required
                      />

                      <p
                        className="
                          mt-2 text-xs leading-5
                          text-[rgb(var(--text-muted))]
                        "
                      >
                        At least 8 characters with an
                        uppercase letter, a number, and a
                        special character.
                      </p>
                    </div>

                    <div>
                      <label
                        htmlFor="confirm-password"
                        className="
                          text-sm font-bold
                          text-[rgb(var(--text-main))]
                        "
                      >
                        Confirm new password
                      </label>

                      <div className="mt-2">
                        <PasswordInput
                          id="confirm-password"
                          type="password"
                          value={confirmPassword}
                          onChange={(e) =>
                            setConfirmPassword(
                              e.target.value
                            )
                          }
                          autoComplete="new-password"
                          required
                        />
                      </div>
                    </div>

                    <PrimaryButton
                      disabled={loading}
                    >
                      {loading
                        ? "Resetting…"
                        : "Reset password"}
                    </PrimaryButton>
                  </form>
                ) : null}
              </>
            )}
          </section>

          <div
            className="
              mt-6 text-center
              text-sm
              text-[rgb(var(--text-muted))]
            "
          >
            <Link
              to="/"
              className="
                font-bold
                text-[rgb(var(--text-main))]
                transition
                hover:text-[rgb(var(--brand))]
              "
            >
              ← Back to Veacon
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}